/**
 * A heritage in three passes over the same material.
 *
 * The photographs come first, because they came first: someone stood there and pressed
 * a shutter, and everything else on the page is downstream of that. Then the places
 * those pictures were turned into, which you can move around inside. Then the objects,
 * each shown beside the single photograph it was generated from — which is the only
 * honest way to show a generation, and also the most interesting way, because the pair
 * is the argument.
 *
 * The credit is not a footnote here. It is in the section's opening sentence, on every
 * photograph, and beside every piece of geometry made from one, because the person who
 * took these has lent them to a commercial pitch and gets his name on all of it.
 */

import {
  creditsFor,
  objectsIn,
  photographFor,
  photographsIn,
  type Photograph,
  type SolidObject,
} from "./collection";
import type { UiCopy } from "./i18n";
import { enqueue, whenReady } from "./mount-queue";
import type { RegionId } from "./regions";

/** Where the published photographs and objects are served from. */
const MEDIA = "collection";

/**
 * Run when the element is one viewport away, once.
 *
 * Deliberately not on page load: five objects and five rooms is a couple of hundred
 * megabytes and ten WebGL contexts, and nobody should pay for the ones they never
 * scroll to. Where there is no IntersectionObserver the caller's button is still
 * sitting there, so the feature degrades to one click rather than to nothing.
 */
function nearby(element: HTMLElement, run: () => void): void {
  if (typeof IntersectionObserver === "undefined") return;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.disconnect();
        run();
      }
    },
    { rootMargin: "100% 0px" },
  );
  observer.observe(element);
}

function creditLink(name: string, url: string): HTMLAnchorElement {
  const link = document.createElement("a");
  link.href = url;
  link.textContent = name;
  link.className = "credit__link";
  // A credit that opens in place loses the reader; one that reaches back into this page
  // is a hole. Neither is necessary to say whose work this is.
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  return link;
}

function plate(photograph: Photograph, copy: UiCopy): HTMLElement {
  const figure = document.createElement("figure");
  figure.className = "plate";
  figure.dataset.reveal = "";

  const image = document.createElement("img");
  image.src = `${MEDIA}/${photograph.file}`;
  // The title is what the picture shows, so it is the alternative text as well; a
  // separate "photograph of…" would only repeat the element's own role.
  image.alt = photograph.title;
  image.loading = "lazy";
  image.decoding = "async";
  figure.append(image);

  const caption = document.createElement("figcaption");
  // The name and the place share a line; everything after them gets its own. Putting
  // all four in one wrapping flex row let the story set itself alongside the title,
  // because a flex item whose width is capped still fits on the line it should have
  // broken out of.
  const line = document.createElement("div");
  line.className = "plate__line";
  const title = document.createElement("span");
  title.className = "plate__title";
  title.textContent = photograph.title;
  line.append(title);
  if (photograph.place !== "") {
    const place = document.createElement("span");
    place.className = "plate__place";
    place.textContent = photograph.place;
    line.append(place);
  }
  caption.append(line);
  const story = document.createElement("p");
  story.className = "plate__story";
  story.textContent = photograph.story;
  caption.append(story);

  const credit = document.createElement("span");
  credit.className = "plate__credit";
  // Singular here. The section's opening line covers all seventeen at once; this one
  // sits under exactly one picture, and "Photographs by" under a single photograph read
  // as a small carelessness on a page whose whole argument is careful attribution.
  credit.append(
    `${copy.collectionCreditOne} `,
    creditLink(photograph.credit.name, photograph.credit.url),
  );
  caption.append(credit);
  figure.append(caption);
  return figure;
}

/**
 * An object and its source photograph, side by side.
 *
 * Shown as a pair on purpose. The geometry alone would invite the reader to assume it
 * was scanned; next to the single picture it was made from, what actually happened is
 * obvious without anyone having to be told twice.
 */
function pair(object: SolidObject, copy: UiCopy): HTMLElement {
  const figure = document.createElement("figure");
  figure.className = "pair";
  figure.dataset.reveal = "";

  const source = photographFor(object.from);
  if (source !== null) {
    const image = document.createElement("img");
    image.className = "pair__source";
    image.src = `${MEDIA}/${source.file}`;
    image.alt = source.title;
    image.loading = "lazy";
    image.decoding = "async";
    figure.append(image);
  }

  const frame = document.createElement("div");
  frame.className = "pair__object";
  const open = document.createElement("button");
  open.type = "button";
  open.className = "pair__open";
  open.textContent = copy.collectionTurn;
  open.setAttribute("aria-label", `${copy.collectionTurn} — ${object.title}`);

  const mount = (): void => {
    if (frame.querySelector("iframe") !== null || frame.dataset.waiting === "true") return;
    frame.dataset.waiting = "true";
    open.remove();
    // The same queue the rooms use. Objects and rooms share a page and a connection
    // budget, so they have to share the turn-taking too.
    enqueue((done) => {
      // See the note in showcase.ts: the flag, not `isConnected`.
      if (frame.dataset.waiting !== "true") {
        done();
        return;
      }
      const viewer = document.createElement("iframe");
      viewer.className = "pair__viewer";
      viewer.src = `splat.html?object=${encodeURIComponent(object.slug)}`;
      viewer.title = object.title;
      viewer.loading = "lazy";
      viewer.setAttribute("allow", "fullscreen; xr-spatial-tracking");
      whenReady(viewer, () => {
        delete frame.dataset.waiting;
        done();
      });
      viewer.addEventListener("error", done, { once: true });
      frame.append(viewer);
    });
  };

  // On approach now, not on press. A button reading "turn it" over a still frame asks
  // the visitor to prove they want the thing they have already scrolled to, and it
  // leaves the section looking like a page of photographs — which is the one thing
  // these are not. The button stays as the fallback where there is no observer.
  open.addEventListener("click", mount);
  frame.append(open);
  nearby(frame, mount);
  figure.append(frame);

  const caption = document.createElement("figcaption");
  const title = document.createElement("span");
  title.className = "plate__title";
  title.textContent = object.title;
  caption.append(title);
  const tale = document.createElement("p");
  tale.className = "plate__story";
  tale.textContent = object.story;
  caption.append(tale);

  const note = document.createElement("span");
  note.className = "pair__note";
  note.append(`${copy.collectionGenerated} `, creditLink(object.credit.name, object.credit.url));
  caption.append(note);
  figure.append(caption);
  return figure;
}

function movement(title: string, className: string): HTMLElement {
  const section = document.createElement("section");
  section.className = `movement ${className}`;
  const heading = document.createElement("h2");
  heading.className = "movement__title";
  heading.dataset.reveal = "";
  heading.textContent = title;
  section.append(heading);
  return section;
}

/**
 * The whole collection for one heritage, or nothing at all if it has none. Returning a
 * fragment rather than a section lets the caller drop it into the story run without an
 * extra wrapper that the scroll machinery would then have to be taught to ignore.
 */
export function createCollection(
  copy: UiCopy,
  region: RegionId,
  rooms: Node | null = null,
): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const photographs = photographsIn(region);
  const objects = objectsIn(region);
  if (photographs.length === 0 && objects.length === 0) {
    if (rooms !== null) fragment.append(rooms);
    return fragment;
  }

  const opening = document.createElement("section");
  opening.className = "movement movement--opening";
  const eyebrow = document.createElement("p");
  eyebrow.className = "eyebrow";
  eyebrow.dataset.reveal = "";
  eyebrow.textContent = copy.collectionEyebrow;
  opening.append(eyebrow);

  const heading = document.createElement("h2");
  heading.className = "movement__lead";
  heading.dataset.reveal = "";
  heading.textContent = copy.collectionTitle;
  opening.append(heading);

  const intro = document.createElement("p");
  intro.className = "movement__intro";
  intro.dataset.reveal = "";
  intro.textContent = copy.collectionIntro;
  opening.append(intro);

  // Named at the top, before any of the work is shown, rather than under it.
  const credits = creditsFor(region);
  if (credits.length > 0) {
    const line = document.createElement("p");
    line.className = "movement__credit";
    line.dataset.reveal = "";
    line.append(`${copy.collectionCreditLead} `);
    credits.forEach((credit, index) => {
      if (index > 0) line.append(", ");
      line.append(creditLink(credit.name, credit.url));
    });
    opening.append(line);
  }
  fragment.append(opening);

  if (photographs.length > 0) {
    const plates = movement(copy.collectionPlatesTitle, "movement--plates");
    const grid = document.createElement("div");
    grid.className = "plate-grid";
    for (const photograph of photographs) grid.append(plate(photograph, copy));
    plates.append(grid);
    fragment.append(plates);
  }

  // The rooms sit between the flat picture and the object you can turn, because that is
  // the order the material actually deepens in: seen, then entered, then handled.
  if (rooms !== null) fragment.append(rooms);

  if (objects.length > 0) {
    const solids = movement(copy.collectionObjectsTitle, "movement--objects");
    const note = document.createElement("p");
    note.className = "movement__intro";
    note.dataset.reveal = "";
    note.textContent = copy.collectionObjectsIntro;
    solids.append(note);
    for (const object of objects) solids.append(pair(object, copy));
    fragment.append(solids);
  }

  return fragment;
}
